import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '@/contexts/AuthContext';
import type { SocialProvider, Auth0User } from '@/services/interfaces/Auth';
import { getAuth0AuthorizationParams, handleAuth0Error } from '@/lib/auth/authConfig';
import { router, AppRoute } from '@/lib/router';
import { toast } from 'sonner';

export interface UseSocialAuthReturn {
  isLoading: boolean;
  loginWithProvider: (provider: SocialProvider) => Promise<void>;
  isAuth0Loading: boolean;
  isAuth0Authenticated: boolean;
  auth0User: any;
  auth0Error: any;
}

export const useSocialAuth = (): UseSocialAuthReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const { socialLogin, isAuthenticated } = useAuth();
  const {
    loginWithRedirect,
    isLoading: isAuth0Loading,
    isAuthenticated: isAuth0Authenticated,
    user: auth0User,
    error: auth0Error
  } = useAuth0();

  // 使用ref来跟踪是否已经处理过当前的Auth0用户
  const processedUserRef = useRef<string | null>(null);

  // 处理Auth0认证成功
  useEffect(() => {
    // 🔑 只有在用户未登录且Auth0认证成功时才处理社交登录
    // 避免在logout后由于Auth0缓存导致的自动重新登录
    if (isAuth0Authenticated && auth0User && !isAuthenticated) {
      const currentUserSub = auth0User.sub || '';
      const currentRoute = router.getCurrentRoute();

      // 🔑 只有在Auth回调页面才处理社交登录，避免在登录页面自动触发
      if (currentRoute === AppRoute.AUTH_CALLBACK) {
        // 检查是否已经处理过这个用户，避免重复处理
        if (processedUserRef.current !== currentUserSub) {
          console.log('Auth0 user authenticated, processing social login', auth0User);
          processedUserRef.current = currentUserSub;
          handleSocialLoginSuccess(auth0User);
        }
      } else {
        console.log('Auth0 user detected but not in callback page, skipping social login');
      }
    }
  }, [isAuth0Authenticated, auth0User, isAuthenticated]);

  // 当用户登录成功后，清除处理记录，为下次登录做准备
  useEffect(() => {
    if (isAuthenticated) {
      processedUserRef.current = null;
    }
  }, [isAuthenticated]);

  // 处理Auth0错误
  useEffect(() => {
    if (auth0Error) {
      console.error('Auth0 error:', auth0Error);
      toast.error(handleAuth0Error(auth0Error));
    }
  }, [auth0Error]);

  const handleSocialLoginSuccess = useCallback(async (auth0User: Auth0User) => {
    try {
      setIsLoading(true);
      await socialLogin(auth0User);
    } catch (error) {
      console.error('Social login failed:', error);
      toast.error('Social login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [socialLogin]);

  const loginWithProvider = useCallback(async (provider: SocialProvider) => {
    try {
      setIsLoading(true);
      const authParams = getAuth0AuthorizationParams(provider);
      
      await loginWithRedirect({
        authorizationParams: authParams,
      });
    } catch (error) {
      console.error(`Error logging in with ${provider}:`, error);
      toast.error(`${provider} login failed. Please try again.`);
      setIsLoading(false);
    }
  }, [loginWithRedirect]);

  return {
    isLoading: isLoading || isAuth0Loading,
    loginWithProvider,
    isAuth0Loading,
    isAuth0Authenticated,
    auth0User,
    auth0Error,
  };
};
